import { Component } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NamespaceView } from '@core/models/namespaceView.interface';
import { CustomMaterialIconService } from '@core/services/custom-material-icon.service';
import { DomainService } from '@core/services/domain.service';
import { ActiveNamespaceService } from '@core/services/namespace/active-namespace.service';
import { INIT_STATUS, NamespaceInitService } from '@core/services/namespace/namespace-init.service';
import { AwsconfigService } from '@features/authentication/services/awsconfig.service';
import { Store } from '@ngrx/store';
import { selectIsDarkMode } from '@stores/theme-mode/theme-mode.selectors';
import { RouterOutlet } from '@angular/router';
import { TermsOfServiceService } from '@core/services/termsOfService/terms-of-service.service';
import { MatDialog } from '@angular/material/dialog';
import { TermsAgreeDialogComponent } from '@features/instances/components/dialogs/terms-agree-dialog/terms-agree-dialog.component';
import { WhoamiService } from '@core/services/whoami.service';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '@core/services/language.service';
import { HotjarService } from '@core/services/hotjar.service';
import { ProfilesService } from '@features/profiles/services/profiles.service';
import { NAMESPACE_ALIASES } from '@core/constants/namespace-aliases';

@Component({
    selector: 'app-root',
    template: '<router-outlet></router-outlet>',
    standalone: true,
    imports: [RouterOutlet, TranslateModule],
})
export class PagesRouterOutletComponent {
  favIcon: HTMLLinkElement = document.querySelector("#appIcon") ?? new HTMLLinkElement();
  userTermsVersion = "";

  constructor(
    private store: Store,
    private customMaterialIconService: CustomMaterialIconService,
    private namespaceService: NamespaceInitService,
    private termService: TermsOfServiceService,
    private dialogService: MatDialog,
    private activeNamespaceService: ActiveNamespaceService,
    private profilesService: ProfilesService,
    private hotjarService: HotjarService,
    private titleService: Title,
    private awsService: AwsconfigService,
    private _domainService: DomainService,
    private awsConfigService: AwsconfigService,
    private languageService: LanguageService,
  ) {
    this.setThemeMode();
    this.customMaterialIconService.registerCustomIcons();
    this._restoreAppleTitleFromCache();
    this.initNamespace();
    const initLanguage = this.languageService.getLocale();
    this.languageService.setLocale(initLanguage);
  }

  setThemeMode() {
    const isDarkMode$ = this.store.select(selectIsDarkMode);
    isDarkMode$.subscribe((isDarkMode) => {
      document.body.setAttribute('theme-mode', isDarkMode);
    });
  }

  initNamespace(): void {
    localStorage.setItem('initializationStatus', INIT_STATUS.IN_PROGRESS);
    const domainName = this._domainService.getCurrentDomainName();
    this.namespaceService.initNamespaceByDomain(domainName).subscribe({
      next: (namespaceInfo: NamespaceView) => {
        this._setupNamespace(namespaceInfo);
        this._updateManifest(namespaceInfo);

        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then(registrations => {
            for (let reg of registrations) {
              reg.update();
            }
          });
        }

        this.awsConfigService.applyConfigLoginPassword();
        localStorage.setItem('initializationStatus', INIT_STATUS.RESOLVED);
        if (namespaceInfo.theme) {
          this.namespaceService.setThemeLink(namespaceInfo.theme);
        }
        setTimeout(() => {
          this.hotjarService.init(namespaceInfo.name);
        }, 1000);
        WhoamiService.subscribeCurrentUser((userId: string) => {
          if (userId) {
            this.termService.getLatestTermsVersionApi(namespaceInfo?.id).subscribe((res) => {
              this.profilesService.getUserInfo().subscribe(userInfo => {
                this.userTermsVersion = userInfo.termsOfServiceVersion;
                if (res.version !== this.userTermsVersion) {
                  this.dialogService.open(
                    TermsAgreeDialogComponent,
                    {disableClose: true, data: {terms: res}}
                  );
                }
              });
            })
          }
        });
      },
      error: () => {
        localStorage.setItem('initializationStatus', INIT_STATUS.REJECTED)
      },
    });
  }

  private _setupNamespace(namespaceInfo: NamespaceView): void {
    this.awsService.configureAws(namespaceInfo);
    this.favIcon.href = namespaceInfo.iconUrl || namespaceInfo.url;
    this.titleService.setTitle(namespaceInfo?.name);
    this.activeNamespaceService.setNamespace(namespaceInfo);
  }

  private _updateManifest(namespaceInfo: NamespaceView): void {
    const existing = document.querySelector('link[rel="manifest"]');
    if (existing) existing.remove();

      let iconUrl = '';
      let iconUrlSmall = '';
      let iconUrlMedium = '';

      const rawNamespace = namespaceInfo.name.toLowerCase().replace(/\s+/g, '_');
      const namespace = NAMESPACE_ALIASES[namespaceInfo.id] ?? rawNamespace;

      iconUrl = `${window.location.origin}/assets/images/pwa/${namespace}-512x512.png`;
      iconUrlMedium = `${window.location.origin}/assets/images/pwa/${namespace}-192x192.png`;
      iconUrlSmall = `${window.location.origin}/assets/images/pwa/${namespace}-180x180.png`;
    
      const manifest = {
        name: namespaceInfo.name,
        short_name: namespaceInfo.name,
        icons: [
          { src: iconUrlSmall, sizes: "180x180", type: "image/png" },
          { src: iconUrlMedium, sizes: "192x192", type: "image/png" },
          { src: iconUrl, sizes: "512x512", type: "image/png" },
        ],
        theme_color: "#1343D3",
        background_color: "#ffffff",
        display: "standalone",
        start_url: "/"
      };

      const manifestJson = JSON.stringify(manifest);
      const dataUrl = `data:application/manifest+json,${encodeURIComponent(manifestJson)}`;

      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = dataUrl;
      document.head.appendChild(link);

      this._updateManifestInSwCache(manifestJson);

      let appleTitleMeta = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
      if (!appleTitleMeta) {
        appleTitleMeta = document.createElement('meta');
        appleTitleMeta.name = 'apple-mobile-web-app-title';
        document.head.appendChild(appleTitleMeta);
      }
      appleTitleMeta.content = namespaceInfo.name;
      localStorage.setItem('namespaceName', namespaceInfo.name);

      let appleTouchIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
      if (!appleTouchIcon) {
        appleTouchIcon = document.createElement('link');
        appleTouchIcon.rel = 'apple-touch-icon';
        document.head.appendChild(appleTouchIcon);
      }
      appleTouchIcon.href = iconUrlSmall;
  }

  private _restoreAppleTitleFromCache(): void {
    const cached = localStorage.getItem('namespaceName');
    if (!cached) return;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    if (meta) meta.content = cached;
  }

  /**
   * Overwrites the manifest.webmanifest entry in every service-worker cache
   * so that iOS — which ignores data: URL manifests and reads the cached static
   * file — receives the correct name/short_name from namespaceInfo.
   */
  private _updateManifestInSwCache(manifestJson: string): void {
    if (!('caches' in window)) return;
    const makeResponse = () => new Response(manifestJson, {
      headers: { 'Content-Type': 'application/manifest+json' }
    });
    caches.keys().then(names =>
      Promise.all(
        names.map(name =>
          caches.open(name).then(cache =>
            cache.match('/manifest.webmanifest').then(match => {
              if (match) return cache.put('/manifest.webmanifest', makeResponse());
              return undefined;
            })
          )
        )
      )
    ).catch(() => { /* non-critical */ });
  }
}